import { Suspense, lazy } from "react";
import {
    BrowserRouter,
    Navigate,
    Outlet,
    Route,
    Routes,
    useParams,
} from "react-router-dom";

import RouteChangeLoading from "../component/RouteChangeLoading";
import SimpleBackdrop from "../component/loading/loading";
import { usePermissions } from "../contexts/permissionsContext";
import { RouteChangeProvider } from "../contexts/routeChangeContextProvider";
import useAuth from "../hooks/useAuth";
import { LoadingSpinner } from "../shared/components/feedback/loading";
import ProtectedLayout from "../shared/components/layout/protected-layout";
import { hasAnyPermissionAction } from "../shared/components/layout/sidebar/utils/permissions.js";

// Lazy loading dos componentes
const NewAuditSaleReopenLayout = lazy(() => import("../view/audit/new-layout/page"));
const DashboardScp = lazy(() => import("../view/Dashboard-scp/dashboard-scp"));
const ReportLinkViewerPage = lazy(() => import("../view/report-link/report-link-viewer-page"));
const NewAvailability = lazy(() => import("../view/availability/insert-availability"));
const CircuitListPage = lazy(() => import("../component/features/circuit/components/circuit-list-page"));
const CircuitFormPage = lazy(() => import("../component/features/circuit/components/circuit-form-page"));
const InsertConfiguration = lazy(() => import("../view/configuration/insert-configuration-page"));
const ListConfiguration = lazy(() => import("../view/configuration/list-configuration"));
const AdminPanelLayout = lazy(() => import("../view/admin-panel/page"));
const NewCustomer = lazy(() => import("../view/customer/insert-customer"));
const NewCustomerListDashboardLayout = lazy(() => import("../view/customer/new-layout/page"));
const NewCustomerLayout = lazy(() => import("../view/customer/new-layout/new-customer/insert-customer"));
const DashboardSuporte = lazy(() => import("../view/dash-suport/dashboad-suporte"));
const ForgotPassword = lazy(() => import("../view/forgot-password"));
const Home = lazy(() => import("../view/home/home"));
const SignIn = lazy(() => import("../view/login/login"));
const NotFound = lazy(() => import("../_not-found"));
const ResetPassword = lazy(() => import("../view/reset-password"));
const PanelListPage = lazy(() => import("../component/features/panel/components/panel-list-page"));
const PanelFormPage = lazy(() => import("../component/features/panel/components/panel-form-page"));
const NewSale = lazy(() => import("../view/sale/page"));
const NewSaleListDashboardLayout = lazy(() => import("../view/sale/new-layout/page"));
const SaleFormPage = lazy(() => import("../view/sale/new-layout/new-sale/page"));
const CampaignMetricsPage = lazy(() =>
  import("../component/features/sale/components/campaign-metrics/campaign-metrics-page")
);
const RecordInvoice = lazy(() => import("../view/record-invoice/record-invoice"));
const ServiceListPage = lazy(() => import("../component/features/service/components/service-list-page"));
const ServiceFormPage = lazy(() => import("../component/features/service/components/service-form-page"));
// const NewUser = lazy(() => import("../view/user/insert-user"));
const UserListPage = lazy(() => import("../component/features/user/components/user-list-page"));
const DisponibilidadePage = lazy(() => import("../view/budget/new-layout/page"));
const ProgrammingPage = lazy(() =>
  import("../component/features/programming/components/programming-page")
);
const UserFormPage = lazy(() => import("../component/features/user/components/user-form-page"));

const Loading = () => {
    return <SimpleBackdrop open={true} title="Carregando informações..." />;
}

/** Redireciona rotas legadas `/user/:id` e `/newUser/:id` para `/usuarios/:id`. */
function LegacyUserEditRedirect() {
    const { id } = useParams();
    return <Navigate to={`/usuarios/${id}`} replace />;
}

/** Redireciona rota legada `/panel/:id` para `/paineis/:id/editar`. */
function PanelLegacyEditRedirect() {
    const { id } = useParams();
    return <Navigate to={`/paineis/${id}/editar`} replace />;
}

/** Redireciona rota legada `/service/:id` para `/servicos/:id/editar`. */
function ServiceLegacyEditRedirect() {
    const { id } = useParams();
    return <Navigate to={`/servicos/${id}/editar`} replace />;
}

/** Redireciona rota legada `/circuit/:id` para `/circuitos/:id/editar`. */
function CircuitLegacyEditRedirect() {
    const { id } = useParams();
    return <Navigate to={`/circuitos/${id}/editar`} replace />;
}

// Fallback só para rotas públicas (login etc.) — fora do AppLayout
const SuspenseFallback = () => {
    return <SimpleBackdrop open={true} title="Carregando página..." />;
}

/** Fallback local do Outlet: Sidebar/TopBar permanecem montados */
const OutletFallback = () => {
    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                minHeight: 240,
                height: "100%",
                width: "100%",
            }}
        >
            <LoadingSpinner size="lg" message="Carregando..." />
        </div>
    );
};

/**
 * @function LayoutShell
 * @description Encapsula rotas autenticadas com o layout principal (sidebar + área de conteúdo).
 * Suspense fica DENTRO do shell para não desmontar Sidebar/TopBar ao carregar chunk lazy.
 * @returns {JSX.Element}
 */
const LayoutShell = () => {
    return (
        <ProtectedLayout>
            <div style={{ height: "100%", overflowY: "auto" }}>
                <Suspense fallback={<OutletFallback />}>
                    <Outlet />
                </Suspense>
            </div>
        </ProtectedLayout>
    );
}

/**
 * @function ProtectedRoute
 * @description Rota protegida que verifica permissão e redireciona se necessário
 * @param {Object} Item - Componente a ser renderizado
 * @param {string} [permission] - Permissão a ser verificada
 * @param {string[]} [roles] - Papéis alternativos (qualquer um)
 * @param {string} action - Ação específica (islist, isinsert, etc.)
 * @param {string} redirectTo - Rota de redirecionamento
 */
const ProtectedRoute = ({ Item, permission, roles, action = 'islist', redirectTo = '/dashboard', children }) => {
    const { signed, loading } = useAuth();
    const { hasPermissionAction, isLoading, permissions } = usePermissions();

    // Aguarda o carregamento da autenticação
    if (loading) {
        return Loading();
    }

    const tokenExists = !!localStorage.getItem("user_token");

    if (!signed || !tokenExists) {
        return <Navigate to="/login" replace />;
    }

    // Aguarda o carregamento das permissões
    if (isLoading) {
        return Loading();
    }

    if (Array.isArray(roles) && roles.length > 0) {
        if (!hasAnyPermissionAction(permissions, roles, action)) {
            return <Navigate to={redirectTo} replace />;
        }
    } else if (permission && !hasPermissionAction(permission, action)) {
        return <Navigate to={redirectTo} replace />;
    }

    return children ? children : <Item />;
}

const RouteApp = () => {
    return (
        <BrowserRouter>
            <RouteChangeProvider>
                <RouteChangeLoading />
                <Suspense fallback={<SuspenseFallback />}>
                    <Routes>
                    <Route path="/login" element={<SignIn />} />
                    <Route path="/forgot-password" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route element={<ProtectedRoute Item={LayoutShell} />}>
                        {/* Rotas públicas */}
                        <Route exact path="/" element={<ProtectedRoute Item={Home} />} />
                        <Route exact path="/dashboard" element={<ProtectedRoute Item={Home} />} />

                        {/* Rotas de Usuário */}
                        <Route exact path="/usuarios" element={<ProtectedRoute Item={UserListPage} permission="Usuario" action="islist" />} />
                        <Route exact path="/usuarios/novo" element={<ProtectedRoute Item={UserFormPage} permission="Usuario" action="isinsert" />} />
                        <Route exact path="/usuarios/:id" element={<ProtectedRoute Item={UserFormPage} permission="Usuario" action="islist" />} />
                        <Route
                            exact
                            path="/user"
                            element={
                                <ProtectedRoute permission="Usuario" action="islist">
                                    <Navigate to="/usuarios" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/userList"
                            element={
                                <ProtectedRoute permission="Usuario" action="islist">
                                    <Navigate to="/usuarios" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/user/new"
                            element={
                                <ProtectedRoute permission="Usuario" action="isinsert">
                                    <Navigate to="/usuarios/novo" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/newUser"
                            element={
                                <ProtectedRoute permission="Usuario" action="isinsert">
                                    <Navigate to="/usuarios/novo" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/user/:id"
                            element={
                                <ProtectedRoute permission="Usuario" action="islist">
                                    <LegacyUserEditRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/newUser/:id"
                            element={
                                <ProtectedRoute permission="Usuario" action="islist">
                                    <LegacyUserEditRedirect />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rotas de Painel */}
                        <Route exact path="/paineis" element={<ProtectedRoute Item={PanelListPage} permission="Painel" action="islist" />} />
                        <Route exact path="/paineis/novo" element={<ProtectedRoute Item={PanelFormPage} permission="Painel" action="isinsert" />} />
                        <Route exact path="/paineis/:id/editar" element={<ProtectedRoute Item={PanelFormPage} permission="Painel" action="islist" />} />
                        <Route
                            exact
                            path="/listPanel"
                            element={
                                <ProtectedRoute permission="Painel" action="islist">
                                    <Navigate to="/paineis" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/panel/:id"
                            element={
                                <ProtectedRoute permission="Painel" action="islist">
                                    <PanelLegacyEditRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/panel"
                            element={
                                <ProtectedRoute permission="Painel" action="isinsert">
                                    <Navigate to="/paineis/novo" replace />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rotas de Cliente */}
                        <Route
                            exact
                            path="/listCustomer"
                            element={
                                <ProtectedRoute permission="Cliente" action="islist">
                                    <Navigate to="/clientes" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route exact path="/customer/:id" element={<ProtectedRoute Item={NewCustomer} permission="Cliente" action="islist" />} />
                        <Route exact path="/customer" element={<ProtectedRoute Item={NewCustomer} permission="Cliente" action="isinsert" />} />
                        <Route exact path="/clientes" element={<ProtectedRoute Item={NewCustomerListDashboardLayout} permission="Cliente" action="islist" />} />
                        <Route exact path="/clientes/novo" element={<ProtectedRoute Item={NewCustomerLayout} permission="Cliente" action="isinsert" />} />
                        <Route exact path="/clientes/:id/editar" element={<ProtectedRoute Item={NewCustomerLayout} permission="Cliente" action="islist" />} />

                        {/* Rotas de Serviço */}
                        <Route exact path="/servicos" element={<ProtectedRoute Item={ServiceListPage} permission="Serviço" action="islist" />} />
                        <Route exact path="/servicos/novo" element={<ProtectedRoute Item={ServiceFormPage} permission="Serviço" action="isinsert" />} />
                        <Route exact path="/servicos/:id/editar" element={<ProtectedRoute Item={ServiceFormPage} permission="Serviço" action="islist" />} />
                        <Route
                            exact
                            path="/listService"
                            element={
                                <ProtectedRoute permission="Serviço" action="islist">
                                    <Navigate to="/servicos" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/service/:id"
                            element={
                                <ProtectedRoute permission="Serviço" action="islist">
                                    <ServiceLegacyEditRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/service"
                            element={
                                <ProtectedRoute permission="Serviço" action="isinsert">
                                    <Navigate to="/servicos/novo" replace />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rotas de Circuito */}
                        <Route exact path="/circuitos" element={<ProtectedRoute Item={CircuitListPage} permission="Circuito" action="islist" />} />
                        <Route exact path="/circuitos/novo" element={<ProtectedRoute Item={CircuitFormPage} permission="Circuito" action="isinsert" />} />
                        <Route exact path="/circuitos/:id/editar" element={<ProtectedRoute Item={CircuitFormPage} permission="Circuito" action="islist" />} />
                        <Route
                            exact
                            path="/listCircuit"
                            element={
                                <ProtectedRoute permission="Circuito" action="islist">
                                    <Navigate to="/circuitos" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/circuit/:id"
                            element={
                                <ProtectedRoute permission="Circuito" action="islist">
                                    <CircuitLegacyEditRedirect />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            exact
                            path="/circuit"
                            element={
                                <ProtectedRoute permission="Circuito" action="isinsert">
                                    <Navigate to="/circuitos/novo" replace />
                                </ProtectedRoute>
                            }
                        />

                        {/* Rotas de Vendas */}
                        <Route
                            exact
                            path="/listSale"
                            element={
                                <ProtectedRoute permission="Vendas" action="islist">
                                    <Navigate to="/vendas" replace />
                                </ProtectedRoute>
                            }
                        />
                        <Route exact path="/sale/:id" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="islist" />} />
                        <Route exact path="/saleReopen/:id/:isOpen" element={<ProtectedRoute Item={NewSale} permission="Reabrir Vendas" action="isinsert" />} />
                        <Route path="/saleParams" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="isinsert" />} />
                        <Route exact path="/sale" element={<ProtectedRoute Item={NewSale} permission="Vendas" action="isinsert" />} />
                        <Route exact path="/vendas" element={<ProtectedRoute Item={NewSaleListDashboardLayout} permission="Vendas" action="islist" />} />
                        <Route exact path="/vendas/nova" element={<ProtectedRoute Item={SaleFormPage} permission="Vendas" action="isinsert" />} />
                        <Route exact path="/vendas/duplicar" element={<ProtectedRoute Item={SaleFormPage} permission="Vendas" action="isinsert" />} />
                        <Route exact path="/vendas/:id/editar" element={<ProtectedRoute Item={SaleFormPage} permission="Vendas" action="islist" />} />
                        <Route exact path="/vendas/:id/reabrir" element={<ProtectedRoute Item={SaleFormPage} permission="Reabrir Vendas" action="isinsert" />} />
                        <Route
                          exact
                          path="/vendas/:id/metricas"
                          element={
                            <ProtectedRoute
                              Item={CampaignMetricsPage}
                              permission="Metricas Venda"
                              action="islist"
                            />
                          }
                        />

                        {/* Rotas de Registro de Fatura */}
                        <Route
                          exact
                          path="/record-invoice"
                          element={
                            <ProtectedRoute
                              Item={RecordInvoice}
                              permission="Escrituracao Nota Fiscal"
                              action="isinsert"
                            />
                          }
                        />

                        {/* Rotas de Disponibilidade */}
                        <Route exact path="/availability" element={<ProtectedRoute Item={NewAvailability} />} />
                        <Route exact path="/availability/:id" element={<ProtectedRoute Item={NewAvailability} />} />
                        {/* Rotas de Dashboard */}
                        <Route exact path="/dashboardscp" element={<ProtectedRoute Item={DashboardScp} permission="Dashboard SCP" action="isinsert" />} />
                        <Route exact path="/relatorios/:slug" element={<ProtectedRoute Item={ReportLinkViewerPage} />} />
                        <Route exact path="/dashboardSuport" element={<ProtectedRoute Item={DashboardSuporte} permission="Suporte" action="isinsert" />} />

                        {/* Rotas de Configuração */}
                        <Route exact path="/painel-administrador" element={<ProtectedRoute Item={AdminPanelLayout} permission="Configuração" action="islist" />} />
                        <Route exact path="/listConfiguration" element={<ProtectedRoute Item={ListConfiguration} permission="Configuração" action="islist" />} />
                        <Route exact path="/configuration" element={<ProtectedRoute Item={InsertConfiguration} permission="Configuração" action="isinsert" />} />
                        <Route exact path="/configuration/:id" element={<ProtectedRoute Item={InsertConfiguration} permission="Configuração" action="islist" />} />

                        {/* Rotas de Auditoria */}
                        <Route exact path="/auditoria/reabertura-vendas" element={<ProtectedRoute Item={NewAuditSaleReopenLayout} permission="Vendas" action="islist" />} />

                        {/* Rotas de Programação (Opec) */}
                        <Route
                            exact
                            path="/programatica"
                            element={
                                <ProtectedRoute
                                    Item={ProgrammingPage}
                                    permission="Visualizar Campanhas 4yousee"
                                    action="islist"
                                />
                            }
                        />

                        {/* Rotas de Orçamento */}
                        <Route exact path="/orcamento" element={<ProtectedRoute Item={DisponibilidadePage} />} />

                        <Route
                            exact
                            path="/budgetList"
                            element={
                                <ProtectedRoute>
                                    <Navigate to="/orcamento" replace />
                                </ProtectedRoute>
                            }
                        />

                        <Route path="*" element={<NotFound />} />
                    </Route>
                    </Routes>
                </Suspense>
            </RouteChangeProvider>
        </BrowserRouter>
    )
}

export default RouteApp;